import { useCallback, useEffect, useRef, useState } from "react";
import { useFormStore } from "../store/formStore";
import { apiFetch } from "../api/apiFetch";

/**
 * Response shape from Optimyl.CRM.Function — GET /jurisdiction/{postalCode}.
 * Backed by vlocity_ins__Jurisdiction__c — the same SF object IHQ v2 + BenAdmin
 * read for zip→city/state.
 */
interface JurisdictionResponse {
  postalCode: string;
  states: Array<{
    state: string;
    stateAbbr: string;
  }>;
  cities: Array<{
    city: string;
    isPreferredCity: boolean;
    stateAbbr: string;
  }>;
}

export interface CityOption {
  city: string;
  stateAbbr: string;
  stateName: string;
}

export interface ZipLookupState {
  loading: boolean;
  /** All city+state pairs for the last-resolved zip, preferred-first. */
  cityOptions: CityOption[];
  /** All states (full names) for the zip — usually 1, rarely 2 for cross-border zips. */
  stateOptions: string[];
  error: string | null;
}

/**
 * Zip → City/State auto-populate hook. `lookup(zip)` fetches the jurisdiction
 * endpoint and writes city + state into the form store; multi-city zips expose
 * the full list via `cityOptions` for a UI dropdown. A passive mount-time
 * lookup populates `cityOptions` without writing, so the City dropdown can
 * render for multi-city zips after a page reload.
 */
export function useZipLookup(): ZipLookupState & { lookup: (zip: string) => void } {
  const setField = useFormStore((s) => s.setField);
  const [state, setState] = useState<ZipLookupState>({ loading: false, cityOptions: [], stateOptions: [], error: null });
  // Seed once from the store so a blur on an unchanged prefilled zip is a no-op.
  const lastZipRef = useRef<string>(useFormStore.getState().data.zipCode || "");

  // Don't clear city/state in the store while loading — the visual blank
  // comes from TextInput's `loading` prop, and clearing would clobber the
  // existing values and surface a transient mismatch hint on lookup failure.
  const fetchAndApply = useCallback(
    async (zip: string, populateFields: boolean) => {
      setState({ loading: true, cityOptions: [], stateOptions: [], error: null });
      try {
        const body = await apiFetch<JurisdictionResponse>(`/jurisdiction/${zip}`);
        // Stale-response guard: a newer lookup has superseded this one.
        if (zip !== lastZipRef.current) return;

        const stateNameByAbbr = new Map<string, string>(
          (body.states ?? [])
            .filter((s) => s.stateAbbr && s.state)
            .map((s) => [s.stateAbbr, s.state] as const),
        );

        // Each entry is a unique city+state pair (backend dedupes by city|stateAbbr).
        const cityOptions: CityOption[] = (body.cities ?? [])
          .sort((a, b) => Number(b.isPreferredCity) - Number(a.isPreferredCity))
          .map((c) => ({
            city: c.city,
            stateAbbr: c.stateAbbr,
            stateName: stateNameByAbbr.get(c.stateAbbr) ?? c.stateAbbr,
          }));

        if (populateFields) {
          if (cityOptions.length > 0) {
            setField("city", cityOptions[0].city);
            if (cityOptions[0].stateAbbr) setField("state", cityOptions[0].stateName);
          } else {
            setField("city", "");
            setField("state", "");
          }
        }

        const stateOptions = Array.from(new Set(
          (body.states ?? []).map((s) => s.state || s.stateAbbr).filter(Boolean)
        ));

        setState({
          loading: false,
          cityOptions,
          stateOptions,
          error: populateFields && cityOptions.length === 0 ? "No cities found for this zip" : null,
        });
      } catch (err) {
        if (zip !== lastZipRef.current) return;
        const msg = err instanceof Error ? err.message : "Zip lookup failed";
        setState({
          loading: false,
          cityOptions: [],
          stateOptions: [],
          // Mount-time passive lookups don't surface failures — the user
          // hasn't done anything to provoke them and the fields are
          // already populated.
          error: populateFields ? msg : null,
        });
      }
    },
    [setField],
  );

  const lookup = useCallback(
    async (zip: string) => {
      if (!/^\d{5}$/.test(zip)) {
        // The zip is now incomplete/empty. If a valid zip had previously
        // resolved (lastZipRef is non-empty), the user has cleared or
        // shortened it — wipe the auto-populated city/state and the option
        // lists so the baseline diff registers the change instead of treating
        // the stale values as a match. Guarding on lastZipRef keeps a
        // mount-time empty zip (with a baseline-supplied city) untouched.
        if (lastZipRef.current) {
          setField("city", "");
          setField("state", "");
          setState({ loading: false, cityOptions: [], stateOptions: [], error: null });
        }
        lastZipRef.current = "";
        return;
      }
      if (zip === lastZipRef.current) return;
      lastZipRef.current = zip;
      await fetchAndApply(zip, /* populateFields */ true);
    },
    [fetchAndApply, setField],
  );

  // Passive lookup on mount: if a zip is already saved, populate cityOptions.
  // If city is blank (e.g., account baseline didn't have a city for this zip),
  // also populate city/state fields so multi-city zips get resolved.
  useEffect(() => {
    const savedZip = lastZipRef.current;
    const savedCity = useFormStore.getState().data.city;
    if (/^\d{5}$/.test(savedZip)) {
      fetchAndApply(savedZip, /* populateFields */ !savedCity);
    }
    // Intentionally run-once-on-mount; lastZipRef and fetchAndApply are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { ...state, lookup };
}
